import React, { useMemo, useState } from "react";
import {
    SalespersonCustomerSearch,
    SALESPERSON_GUID,
    SALESPERSON_ID,
    SALESPERSON_PERSON_NAME,
} from "./salesperson-customer-search";
import { Checkbox, DialogBox, DialogBoxFooterType, ZeroToInfiniteGrid } from "~/core";
import { messages } from "./../i18n-messages";
import { CustomerAPI } from "@ai360/core";
import { useIntl } from "react-intl";
import { PROPS_IS_PRIMARY } from "~/admin/setup/component/email/constants";

interface IProps {
    classNames: string;
    itemList: CustomerAPI.ISalespersonCustomer[];
    onSelectionChange: (value: CustomerAPI.ISalespersonCustomer[]) => void;
    record: CustomerAPI.ISalespersonCustomer[];
    deleteConfirmation: () => void;
}

function getSelectedOptionList(
    itemList: CustomerAPI.ISalespersonCustomer[],
    selectedItems: CustomerAPI.ISalespersonCustomer[],
    selected = true
) {
    return (
        itemList?.filter((item) => {
            const match = selectedItems.some((selectedItem) => {
                return selectedItem[SALESPERSON_GUID] === item[SALESPERSON_GUID];
            });
            return (selected && match) || (!selected && !match);
        }) || []
    );
}

function onChange(
    item: any,
    isAdd: boolean,
    record: CustomerAPI.ISalespersonCustomer[],
    onSelectionChange: (value: CustomerAPI.ISalespersonCustomer[]) => void
) {
    if (item == null) {
        return;
    }
    const value = item[SALESPERSON_GUID];
    const selectedValues = [...record];
    let selectedIndex = -1;
    const isAlreadyInList = selectedValues.some((listItem, index) => {
        if (listItem[SALESPERSON_GUID] === value) {
            selectedIndex = index;
        }
        return listItem[SALESPERSON_GUID] === value;
    });

    if (isAdd && !isAlreadyInList) {
        item[PROPS_IS_PRIMARY] = record.length === 0;
        selectedValues.push(item);
    } else if (!isAdd && isAlreadyInList && !item.isPrimary) {
        selectedValues.splice(selectedIndex, 1);
    }
    onSelectionChange(selectedValues);
}

export function SalespersonCustomerList(props: IProps) {
    const { record = [], itemList = [], onSelectionChange } = props;
    const { formatMessage } = useIntl();

    const [newPrimaryGuid, setNewPrimaryGuid] = useState<string>(null);

    const currentList = useMemo(
        () => getSelectedOptionList(itemList, record, false),
        [itemList, record]
    );
    const gridRecord = useMemo<any[]>(
        () =>
            record.map((item) => ({
                ...item,
                restrictEditDelete: item.isPrimary,
            })),
        [record]
    );

    function renderPrimary(value, record) {
        return (
            <Checkbox
                key={record.orgLevelGuid}
                className="agvance-checkbox"
                value={record[PROPS_IS_PRIMARY]}
                onChange={() => {
                    if (!record[PROPS_IS_PRIMARY]) {
                        setNewPrimaryGuid(record[SALESPERSON_GUID]);
                    }
                }}
                testId={`salesperson-customer-is-primary-checkbox-${record.orgLevelGuid}`}
            />
        );
    }

    return (
        <div className="form-section-child-stretch mini-grid auto-search-list-container">
            <DialogBox
                footerType={DialogBoxFooterType.YES_NO}
                isOpen={newPrimaryGuid != null}
                onAction={() => {
                    const newValues = record.map((r) => ({
                        ...r,
                        isPrimary: r.salespersonGuid === newPrimaryGuid,
                    }));
                    setNewPrimaryGuid(null);
                    props.onSelectionChange(newValues);
                }}
                onClose={() => setNewPrimaryGuid(null)}
                testId="salesperson-customer-primary-change-modal"
                title={formatMessage(messages.primarySalespersonChangeTitle)}
            >
                {formatMessage(messages.primarySalespersonChangeText)}
            </DialogBox>
            {currentList.length === 0 ? null : (
                <SalespersonCustomerSearch
                    classNames={props.classNames}
                    clearOnSelection
                    containerClassName={"org-level-search-container"}
                    itemList={currentList}
                    onSelection={(value) => onChange(value, true, record, onSelectionChange)}
                    placeholderText={formatMessage(messages.addSalesperson)}
                    selectedValue={""}
                />
            )}
            {record && record.length > 0 && (
                <ZeroToInfiniteGrid
                    records={gridRecord}
                    columns={{
                        [SALESPERSON_PERSON_NAME]: {
                            title: formatMessage(messages.name),
                            className: "org-level-name",
                        },
                        [SALESPERSON_ID]: {
                            title: formatMessage(messages.id),
                            className: "org-level-city",
                        },
                        ...(record.length > 1 && {
                            [PROPS_IS_PRIMARY]: {
                                title: formatMessage(messages.primary),
                                className: "org-level-primary",
                                printer: renderPrimary,
                            },
                        }),
                    }}
                    className="org-level-grid"
                    onDelete={(option) => onChange(option, false, record, onSelectionChange)}
                />
            )}
        </div>
    );
}
